import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, message, Modal, Input, Form, Switch, Alert } from 'antd';
import Sidebar from './Sidebar';
import TriggerNode from './nodes/TriggerNode';
import AIModelNode from './nodes/AIModelNode';
import OutputNode from './nodes/OutputNode';
import SendEmailNode from './nodes/SendEmailNode';
import HumanInputNode from './nodes/HumanInputNode';
import ConditionNode from './nodes/ConditionNode';  
import PostNode from './nodes/PostNode';
import TemplateModal from './TemplateModal';
import ParserNode from './nodes/ParserNode';
import ChatNode from './nodes/ChatNode';
import ResearchNode from './nodes/ResearchNode';
import FirecrawlNode from './nodes/FirecrawlNode';
import UnsplashNode from './nodes/UnsplashNode';
import MCPClientNode from './nodes/MCPClientNode';
import CreateFileNode from './nodes/CreateFileNode';
import APICallNode from './nodes/APICallNode';
import MultimediaGeneratorNode from './nodes/MultimediaGeneratorNode';
import { defaultTemplates } from './defaultTemplates';
import WorkflowActionsModal from './WorkflowActionsModal';
import WorkflowNameHeader from './WorkflowNameHeader';
import ExecutionStatusBar from './ExecutionStatusBar';
import WorkflowDetailsModal from './WorkflowDetailsModal';
import WorkflowAssistant from './WorkflowAssistant';
import { AnnotationToolbar, StickyNote, TextAnnotation, Shape } from './AnnotationTools';
import CustomEdge from './CustomEdge';
import axios from 'axios';
import { adminInstance } from './api/axiosConfig';
import config from '../config';
import { SentimentAnalysisNode, SummaryGeneratorNode, ExtractInformationNode, WriteArticleNode, OptimizeSEONode } from './nodes/SpecializedAINodes';
import {BulbOutlined, SunOutlined, MoonOutlined } from '@ant-design/icons';
import EmbedCodeModal from './EmbedCodeModal';
import { LockedNodeOverlay } from './LockedNodeOverlay';

const nodeTypes = {
  // === LITE NODES (no wrapper needed) ===
  trigger: TriggerNode,
  aiModel: AIModelNode,
  post: PostNode,
  unsplash: UnsplashNode,
  research: ResearchNode,
  chat: ChatNode, // Has internal restrictions
  output: OutputNode, // Has internal restrictions
  
  // === ANNOTATION NODES (always allowed) ===
  stickyNote: StickyNote,
  textAnnotation: TextAnnotation,
  shape: Shape,
  
  // === PRO-ONLY NODES (wrapped with LockedNodeOverlay) ===
  humanInput: (props) => (
    <LockedNodeOverlay nodeType="humanInput">
      <HumanInputNode {...props} />
    </LockedNodeOverlay>
  ),
  sendEmail: (props) => (
    <LockedNodeOverlay nodeType="sendEmail">
      <SendEmailNode {...props} />
    </LockedNodeOverlay>
  ),
  condition: (props) => (
    <LockedNodeOverlay nodeType="condition">
      <ConditionNode {...props} />
    </LockedNodeOverlay>
  ),
  sentimentAnalysis: (props) => (
    <LockedNodeOverlay nodeType="sentimentAnalysis">
      <SentimentAnalysisNode {...props} />
    </LockedNodeOverlay>
  ),
  summaryGenerator: (props) => (
    <LockedNodeOverlay nodeType="summaryGenerator">
      <SummaryGeneratorNode {...props} />
    </LockedNodeOverlay>
  ),
  extractInformation: (props) => (
    <LockedNodeOverlay nodeType="extractInformation">
      <ExtractInformationNode {...props} />
    </LockedNodeOverlay>
  ),
  writeArticle: (props) => (
    <LockedNodeOverlay nodeType="writeArticle">
      <WriteArticleNode {...props} />
    </LockedNodeOverlay>
  ),
  optimizeSEO: (props) => (
    <LockedNodeOverlay nodeType="optimizeSEO">
      <OptimizeSEONode {...props} />
    </LockedNodeOverlay>
  ),
  parser: (props) => (
    <LockedNodeOverlay nodeType="parser">
      <ParserNode {...props} />
    </LockedNodeOverlay>
  ),
  firecrawl: (props) => (
    <LockedNodeOverlay nodeType="firecrawl">
      <FirecrawlNode {...props} />
    </LockedNodeOverlay>
  ),
  createFile: (props) => (
    <LockedNodeOverlay nodeType="createFile">
      <CreateFileNode {...props} />
    </LockedNodeOverlay>
  ),
  APICall: (props) => (
    <LockedNodeOverlay nodeType="APICall">
      <APICallNode {...props} />
    </LockedNodeOverlay>
  ),
  mediaGenerator: (props) => (
    <LockedNodeOverlay nodeType="mediaGenerator">
      <MultimediaGeneratorNode {...props} />
    </LockedNodeOverlay>
  ),
  MCPClient: (props) => (
    <LockedNodeOverlay nodeType="MCPClient">
      <MCPClientNode {...props} />
    </LockedNodeOverlay>
  ),
};

const edgeTypes = {
  default: CustomEdge,
};


const WorkflowBuilderInner = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const { setViewport } = useReactFlow();
  const [isExecuting, setIsExecuting] = useState(false);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [isSavedWorkflow, setIsSavedWorkflow] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isTemplateSelectionModalVisible, setIsTemplateSelectionModalVisible] = useState(false);
  const [isSaveTemplateModalVisible, setIsSaveTemplateModalVisible] = useState(false);
  const [isActionsModalVisible, setIsActionsModalVisible] = useState(false);
  const [currentExecutingNode, setCurrentExecutingNode] = useState(null);
  const [completionTime, setCompletionTime] = useState(null);
  const [executionError, setExecutionError] = useState(false);
  const [executionProgress, setExecutionProgress] = useState([]);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [colorMode, setColorMode] = useState('light');
  const workflowAssistantRef = useRef(null);
  const [embedModalVisible, setEmbedModalVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          // Handle settings separately from other data
          const updatedSettings = newData.settings 
            ? { ...node.data.settings, ...newData.settings }
            : node.data.settings;
  
          return {
            ...node,
            data: { 
              ...node.data, 
              ...newData,
              settings: updatedSettings,
              workflowId, // Keep workflowId
              updateNodeData, // Keep function reference
              onDelete: node.data.onDelete, // Keep delete function
            },
          };
        }
        return node;
      })
    );
  }, [setNodes, workflowId]);

  const toggleDarkMode = useCallback(() => {
    setColorMode((prev) => prev === 'light' ? 'dark' : 'light');
  }, []);

  const getSelectedNode = useCallback(() => {
    return nodes.find(node => node.selected);
  }, [nodes]);



  useEffect(() => {
    const savedMode = localStorage.getItem('wp-ai-workflows-color-mode');
    if (savedMode) {
      setColorMode(savedMode);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('wp-ai-workflows-color-mode', colorMode);
  }, [colorMode]);
  
  useEffect(() => {
    console.log("Component mounted. Checking for existing workflow data...");
    const settings = window.wpAiWorkflowsSettings || {};
    const { page, workflowData } = settings;
    
    // Check if coming from setup wizard
    const urlParams = new URLSearchParams(window.location.search);
    const isFromSetup = urlParams.get('template') === 'rss-to-blog' && urlParams.get('from_setup') === 'true';

    if (isFromSetup) {
      console.log("Loading setup template...");
      loadSetupTemplate();
      
      // Clean up URL to remove parameters
      const newUrl = window.location.pathname + '?page=wp-ai-workflows&action=builder';
      window.history.replaceState({}, document.title, newUrl);
    } else if (page === 'edit' && workflowData) {
      console.log("Loading existing workflow...");
      if (Array.isArray(workflowData.nodes)) {
        setNodes(workflowData.nodes.map(node => {
          // Restore handlers for annotation nodes
          if (['stickyNote', 'textAnnotation', 'shape'].includes(node.type)) {
            return {
              ...node,
              data: {
                ...node.data,
                onChange: (id, updates) => {
                  setNodes(nds => 
                    nds.map(n => 
                      n.id === id 
                        ? { 
                            ...n, 
                            data: { 
                              ...n.data, 
                              ...updates,
                              onChange: n.data.onChange,
                              onDelete: n.data.onDelete 
                            } 
                          }
                        : n
                    )
                  );
                },
                onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
              }
            };
          }
          // Handle other node types
          return {
            ...node,
            data: {
              ...node.data,
              updateNodeData,
              onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id)),
              workflowId: workflowData.id
            }
          };
        }));
        setEdges(workflowData.edges || []);
        if (workflowData.name) {
          setWorkflowName(workflowData.name);
        }
        setWorkflowId(workflowData.id || null);
        setIsEditMode(true);
        setIsSavedWorkflow(true);
        if (workflowData.viewport) {
          setViewport(workflowData.viewport);
        }
      }
    } else {
      // Only show template selection if not coming from setup
      setIsTemplateSelectionModalVisible(true);
    }
  }, []);

  useEffect(() => {
    if (reactFlowInstance && workflowAssistantRef.current) {
      workflowAssistantRef.current.updateWorkflowContext({
        nodes,
        edges,
        viewport: reactFlowInstance.getViewport(),
        name: workflowName,
        id: workflowId,
        isExecuting,
        isSaved: isSavedWorkflow
      });
    }
  }, [nodes, edges, workflowName, workflowId, isExecuting, isSavedWorkflow, reactFlowInstance]);

  const loadDefaultTemplate = async (templateId) => {
    const template = defaultTemplates[templateId];
    if (!template) {
      throw new Error('Template not found');
    }
    return template;
  };

  const handleEdgeDelete = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
  }, [setEdges]);

  const handleImport = useCallback((importedData) => {
    try {
      // Convert imported data to nodes with required functions
      const importedNodes = importedData.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          updateNodeData,
          onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
        }
      }));
  
      setNodes(importedNodes);
      setEdges(importedData.edges);
      if (importedData.name) {
        setWorkflowName(importedData.name);
      }
      message.success('Workflow imported successfully');
    } catch (error) {
      message.error('Error importing workflow: ' + error.message);
    }
  }, [setNodes, setEdges, setWorkflowName, updateNodeData]);

  const handleExport = () => {
    // Clean node data by removing execution-related fields
    const cleanedNodes = nodes.map(node => {
      const cleanedNode = { ...node };
      const newData = {};
      
      // Copy only non-execution related fields
      Object.keys(node.data || {}).forEach(key => {
        if (!['output', 'displayOutput', 'isExecuting', 'executed', 'executionError', 
             'executionTime', 'lastExecutionTime', 'crawlResults', 'selectedPages', 
             'executionProgress', 'executionStatus'].includes(key)) {
          newData[key] = node.data[key];
        }
      });
      
      cleanedNode.data = newData;
      return cleanedNode;
    });
  
    const workflowData = {
      name: workflowName || 'Untitled Workflow',
      nodes: cleanedNodes,
      edges: edges,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
  
    const blob = new Blob([JSON.stringify(workflowData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${workflowName || 'workflow'}-${new Date().getTime()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleActionModalOpen = () => {
    setIsActionsModalVisible(true);
  };

  const viewExecutionDetails = async (executionId) => {
    try {
      const response = await adminInstance.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/executions/${executionId}`
      );
      let execution = response.data;
      
      // Process output_data if it's a string
      if (typeof execution.output_data === 'string') {
        try {
          execution.output_data = JSON.parse(execution.output_data);
        } catch (e) {
          console.error('Failed to parse output_data:', e);
          execution.output_data = {};
        }
      }
      
      setSelectedExecution(execution);
      setIsDetailsModalVisible(true);
    } catch (error) {
      console.error('Error fetching execution details:', error);
      message.error('Failed to fetch execution details');
    }
  };

  const handleSelectTemplate = async (templateId, isDefault = false) => {
    try {
      let templateData;
      if (isDefault) {
        templateData = await loadDefaultTemplate(templateId);
      } else {
        // Extract the actual ID by removing the "user-" prefix
        const actualId = templateId.replace('user-', '');
        const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/templates/${actualId}`, {
          headers: { 'X-WP-Nonce': config.apiSettings.nonce }
        });
        templateData = response.data;
      }
      
      // Ensure workflow_data is properly parsed
      let workflowData = templateData.workflow_data;
      if (typeof workflowData === 'string') {
        workflowData = JSON.parse(workflowData);
      }
      
      const updatedNodes = workflowData.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          updateNodeData,
          onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
        }
      }));
  
      setNodes(updatedNodes);
      setEdges(workflowData.edges);
      setIsTemplateSelectionModalVisible(false);
      setIsEditingTemplate(true);
      if (templateData.name) {
        setWorkflowName(templateData.name);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      message.error('Failed to load template');
    }
  };

  const handleCreateNew = () => {
    setIsTemplateSelectionModalVisible(false);
    setIsEditingTemplate(false);
    // Set up initial empty workflow with a timestamp-based ID
    const randomId = `trigger-${Date.now()}`;
    setNodes([
      { 
        id: randomId, 
        type: 'trigger', 
        position: { x: 250, y: 25 }, 
        data: { 
          label: 'Trigger Node', 
          updateNodeData,
          onDelete: (id) => {
            setNodes((nds) => nds.filter((node) => node.id !== id));
          },
          triggerType: '',
          content: '',
          selectedForm: null,
          selectedFields: []
        }
      }
    ]);
    setEdges([]);
  };

  const hasCycle = (graph, start, visited = new Set(), recursionStack = new Set()) => {
    visited.add(start);
    recursionStack.add(start);
  
    // Get all nodes this node connects to
    const neighbors = graph[start] || [];
    
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (hasCycle(graph, neighbor, visited, recursionStack)) {
          return true;
        }
      } else if (recursionStack.has(neighbor)) {
        return true;
      }
    }
  
    recursionStack.delete(start);
    return false;
  };
  
  // Create adjacency list from edges
  const createGraph = (edges) => {
    const graph = {};
    edges.forEach(edge => {
      if (!graph[edge.source]) {
        graph[edge.source] = [];
      }
      graph[edge.source].push(edge.target);
    });
    return graph;
  };
  
  // Check if adding a new edge would create a cycle
  const wouldCreateCycle = (edges, newEdge) => {
    // Add the new edge to existing edges
    const allEdges = [...edges, newEdge];
    const graph = createGraph(allEdges);
    
    // Check for cycles starting from the source of new edge
    return hasCycle(graph, newEdge.source);
  };

  const onConnect = useCallback((params) => {
    // First check if this connection would create a cycle
    if (wouldCreateCycle(edges, params)) {
      message.error('Cannot create connection: This would create a loop in the workflow');
      return;
    }
    
    // If no cycle would be created, add the edge
    setEdges((eds) => addEdge(params, eds));
  }, [edges, setEdges]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleSaveAsTemplate = () => {
    setIsSaveTemplateModalVisible(true);
  };

  const handleTemplateSave = async () => {
    if (!templateName.trim()) {
      message.error('Please enter a template name');
      return;
    }
  
    try {
      const flow = reactFlowInstance.toObject();
      const templateData = {
        name: templateName,
        description: templateDescription,
        workflow_data: JSON.stringify({ nodes: flow.nodes, edges: flow.edges })
      };
      await axios.post(`${config.apiSettings.root}wp-ai-workflows/v1/templates`, templateData, {
        headers: { 'X-WP-Nonce': config.apiSettings.nonce }
      });
      message.success('Template saved successfully');
      setIsSaveTemplateModalVisible(false);
      setTemplateName('');
      setTemplateDescription('');
    } catch (error) {
      console.error('Error saving template:', error);
      message.error('Failed to save template');
    }
  };

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
  
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
  
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
  
      let newNode;
  
      // Handle annotation nodes
      switch (type) {
        case 'stickyNote':
          newNode = {
            id: `note-${Date.now()}`,
            type,
            position,
            data: { 
              content: '',
              color: '#fff9c4',
              size: { width: 200, height: 150 },
              onChange: (id, updates) => {
                setNodes(nds => 
                  nds.map(node => 
                    node.id === id 
                      ? { 
                          ...node, 
                          data: { 
                            ...node.data, 
                            ...updates,
                            onChange: node.data.onChange,
                            onDelete: node.data.onDelete 
                          } 
                        }
                      : node
                  )
                );
              },
              onDelete: (id) => {
                setNodes(nds => nds.filter(node => node.id !== id));
              }
            },
            draggable: true,
            className: 'react-flow-annotation',
            zIndex: -1
          };
          break;
  
        case 'textAnnotation':
          newNode = {
            id: `text-${Date.now()}`,
            type,
            position,
            data: {
              content: '',
              fontSize: 14,
              size: { width: 200, height: 'auto' },
              onChange: (id, updates) => {
                setNodes(nds => 
                  nds.map(node => 
                    node.id === id 
                      ? { 
                          ...node, 
                          data: { 
                            ...node.data, 
                            ...updates,
                            onChange: node.data.onChange,
                            onDelete: node.data.onDelete 
                          } 
                        }
                      : node
                  )
                );
              },
              onDelete: (id) => {
                setNodes(nds => nds.filter(node => node.id !== id));
              }
            },
            draggable: true,
            className: 'react-flow-annotation',
            zIndex: -1
          };
          break;
  
        case 'shape':
          newNode = {
            id: `shape-${Date.now()}`,
            type,
            position,
            data: {
              shapeType: 'rectangle',
              color: '#e8e8e8',
              size: { width: 100, height: 100 },
              onChange: (id, updates) => {
                setNodes(nds => 
                  nds.map(node => 
                    node.id === id 
                      ? { 
                          ...node, 
                          data: { 
                            ...node.data, 
                            ...updates,
                            onChange: node.data.onChange,
                            onDelete: node.data.onDelete 
                          } 
                        }
                      : node
                  )
                );
              },
              onDelete: (id) => {
                setNodes(nds => nds.filter(node => node.id !== id));
              }
            },
            draggable: true,
            className: 'react-flow-annotation',
            zIndex: -1
          };
          break;
  
        default:
          // Handle regular workflow nodes
          newNode = {
            id: `${type}-${Date.now()}`,
            type,
            position,
            draggable: true,
            data: { 
              updateNodeData,
              onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id)),
            },
          };
      }
  
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, updateNodeData]
  );

  const onSave = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const onUpdate = useCallback(async () => {
    if (!workflowId) {
      message.error('Cannot update: No workflow ID found');
      return;
    }
    try {
      const flow = reactFlowInstance.toObject();
      const updatedWorkflow = {
        id: workflowId,
        name: workflowName,
        nodes: flow.nodes,
        edges: flow.edges,
        viewport: flow.viewport
      };
      const response = await axios.put(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`, updatedWorkflow, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      if (response.data) {
        message.success('Workflow updated successfully');
      }
    } catch (error) {
      console.error('Error updating workflow:', error);
      message.error('Failed to update workflow');
    }
  }, [workflowId, workflowName, reactFlowInstance]);

  const handleSaveWorkflow = async () => {
    if (!workflowName.trim()) {
      message.error('Please enter a workflow name');
      return;
    }
    try {
      const method = workflowId ? 'put' : 'post';
      const url = workflowId
        ? `${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`
        : `${config.apiSettings.root}wp-ai-workflows/v1/workflows`;
      
      const flow = reactFlowInstance.toObject();
      
      // Preserve annotation data by explicitly including it
      const annotationNodes = flow.nodes.map(node => {
        if (['stickyNote', 'textAnnotation', 'shape'].includes(node.type)) {
          return {
            ...node,
            data: {
              ...node.data,
              // Preserve only the necessary data properties
              content: node.data.content,
              color: node.data.color,
              size: node.data.size,
              fontSize: node.data.fontSize,
              shapeType: node.data.shapeType
            }
          };
        }
        return node;
      });
  
      const workflowData = {
        id: workflowId,
        name: workflowName,
        nodes: annotationNodes,
        edges: flow.edges,
        viewport: flow.viewport
      };
  
      const response = await axios[method](url, workflowData, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
  
      if (response.data && response.data.id) {
        const newWorkflowId = response.data.id;
        setWorkflowId(newWorkflowId);
        setIsSavedWorkflow(true);
  
        // Update nodes with the new workflowId and preserve annotation data
        const updatedNodes = annotationNodes.map(node => {
          if (node.type === 'output' || node.type === 'chat') {
            return {
              ...node,
              data: {
                ...node.data,
                workflowId: newWorkflowId
              }
            };
          }
          return node;
        });
  
        reactFlowInstance.setNodes(updatedNodes);
      }
  
      message.success('Workflow saved successfully');
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error saving workflow:', error);
      message.error('Failed to save workflow: ' + (error.response?.data?.message || error.message));
    }
  };

  const executeWorkflow = async () => {
    if (!workflowId) {
        message.error('Please save the workflow before executing');
        return;
    }

    setIsExecuting(true);
    setExecutionError(false);
    setCompletionTime(null);
    setExecutionProgress([]);
    
    let pollInterval;
    let timeoutCounter = 0;
    const MAX_TIMEOUT = 600; 

    try {
        const response = await adminInstance.post(
            `${config.apiSettings.root}wp-ai-workflows/v1/execute-workflow/${workflowId}`,
            {
                nodes: nodes,
                edges: edges,
            }
        );

        setExecutionId(response.data.execution_id);

        console.log('Raw response:', response);

        const executionId = response.data.execution_id;

        if (!executionId) {
            throw new Error('Invalid response from server: No execution ID received');
        }

        // Start polling
        pollInterval = setInterval(async () => {
            try {
                timeoutCounter++;
                if (timeoutCounter >= MAX_TIMEOUT) {
                    clearInterval(pollInterval);
                    setExecutionError(true);
                    setIsExecuting(false);
                    setCompletionTime(new Date().toLocaleTimeString());
                    message.error('Execution timed out after 4 minutes');
                    return;
                }

                const statusResponse = await adminInstance.get(
                    `${config.apiSettings.root}wp-ai-workflows/v1/execution-status/${executionId}`
                );

                const { status, current_node, nodes: executedNodes, is_complete} = statusResponse.data;

                if (current_node) {
                    const currentNodeData = nodes.find(n => n.id === current_node);
                    if (currentNodeData) {
                        setCurrentExecutingNode(currentNodeData.data.nodeName || current_node);
                    }
                }

                // Process completed nodes
                if (executedNodes) {
                    const updatedNodes = [...nodes];
                    
                    Object.entries(executedNodes).forEach(([nodeId, nodeData]) => {
                        const nodeIndex = updatedNodes.findIndex(n => n.id === nodeId);
                        if (nodeIndex !== -1) {
                            updatedNodes[nodeIndex] = {
                                ...updatedNodes[nodeIndex],
                                data: {
                                    ...updatedNodes[nodeIndex].data,
                                    displayOutput: nodeData.content,
                                    output: nodeData.content,
                                    isExecuting: false,
                                    executed: true
                                }
                            };
                            
                            const nodeName = updatedNodes[nodeIndex].data.nodeName || nodeId;
                            setExecutionProgress(prev => {
                                if (!prev.includes(nodeName)) {
                                    return [...prev, nodeName];
                                }
                                return prev;
                            });
                        }
                    });

                    setNodes(updatedNodes);
                }

                if (is_complete || status === 'completed' || status === 'error') {
                    clearInterval(pollInterval);
                    setIsExecuting(false);
                    setCurrentExecutingNode(null);
                    setCompletionTime(new Date().toLocaleTimeString());
                    if (status === 'error') {
                        setExecutionError(true);
                        message.error('Workflow execution failed');
                    } else {
                        message.success('Workflow executed successfully');
                    }
                }
            } catch (error) {
                console.error('Error polling execution status:', error);
                clearInterval(pollInterval);
                setExecutionError(true);
                setIsExecuting(false);
                setCompletionTime(new Date().toLocaleTimeString());
                message.error('Error during workflow execution');
            }
        }, 500);

    } catch (error) {
        console.error('Error executing workflow:', error);
        setExecutionError(true);
        setIsExecuting(false);
        setCompletionTime(new Date().toLocaleTimeString());
        message.error('Failed to execute workflow: ' + (error.response?.data?.message || error.message));
    }

    // Return cleanup function
    return () => {
        if (pollInterval) {
            clearInterval(pollInterval);
        }
    };
};

const loadSetupTemplate = () => {
  const template = {
 "name": "RSS to News article",
  "nodes": [
    {
      "id": "trigger-1753200513904",
      "type": "trigger",
      "position": {
        "x": 155.10998860072255,
        "y": -149.1101126592248
      },
      "data": {
        "label": "Trigger Node",
        "triggerType": "rss",
        "content": "",
        "selectedForm": null,
        "selectedFields": [],
        "selectedWorkflow": null,
        "selectedWpCoreTrigger": null,
        "wpCoreTriggerConditions": [],
        "selectedTable": null,
        "databaseTriggerType": null,
        "workflowId": "68811ed025790",
        "rssSettings": {
          "feedUrl": "https://techcrunch.com/feed/",
          "pollingInterval": "Custom",
          "filters": {
            "title": "",
            "content": "",
            "categories": []
          },
          "maxItems": 10,
          "includeContent": false
        },
        "nodeName": "TechCrunch RSS Feed (Start Here)"
      },
      "measured": {
        "width": 380,
        "height": 816
      }
    },
    {
      "id": "aiModel-1753200539633",
      "type": "aiModel",
      "position": {
        "x": 633.2782356508867,
        "y": 153.3462245183194
      },
      "data": {
        "nodeName": "AI Content Generator",
        "imageUrls": [],
        "settings": {
          "temperature": 1,
          "top_p": 1,
          "top_k": 0,
          "frequency_penalty": 0,
          "presence_penalty": 0,
          "repetition_penalty": 1,
          "max_tokens": 4096
        },
        "content": "You are a professional tech news writer creating a WordPress newsletter article.\n\nREQUEST:\nAnalyze the TechCrunch RSS feed below and create a well-structured newsletter article highlighting the 5-7 most significant tech stories.\n\nFORMATTING REQUIREMENTS:\n1. The article needs to be formatted in HTML for a WordPress post\n2. Create a clear headline with H2 tag\n3. Organize content with subheadings using strong tags\n4. Include original publication dates and authors for credibility\n5. Add proper citation links to source articles\n6. Use bullet points for listing related stories\n7. Format for easy scanning with short paragraphs\n\nSEO OPTIMIZATION:\n1. Include relevant tech keywords naturally\n2. Create a hierarchical structure with proper headings\n3. Ensure content is informative and valuable to readers\n\nTONE: Professional but conversational\nLENGTH: 400-600 words (excluding SEO notes)\n\nReturn only the article with nothing before or after it.\n\nRSS FEED DATA:\n[[items] from trigger-1753200513904]",
        "openaiTools": {
          "webSearch": {
            "enabled": false,
            "contextSize": "medium",
            "location": {
              "city": "",
              "region": "",
              "country": ""
            }
          },
          "fileSearch": {
            "enabled": false,
            "vectorStoreId": "",
            "maxResults": 5
          }
        },
        "workflowId": null,
        "model": "google/gemma-3-27b-it:free"
      },
      "measured": {
        "width": 380,
        "height": 473
      }
    },
    {
      "id": "post-1753200661952",
      "type": "post",
      "position": {
        "x": 1619.3689170692844,
        "y": 214.73972715449656
      },
      "data": {
        "nodeName": "WordPress Draft Post (Final Output)",
        "selectedPostType": "post",
        "fieldMappings": {
          "post_content": "[Input from aiModel-1753200539633]",
          "post_title": "[Input from aiModel-1753200684599]",
          "post_excerpt": ""
        },
        "postStatus": "draft",
        "editorPreferences": [],
        "featuredImage": {
          "url": "",
          "id": null,
          "source": null
        },
        "productImages": [],
        "selectedAuthor": "",
        "selectedCategories": [],
        "workflowId": null
      },
      "measured": {
        "width": 400,
        "height": 626
      },
      "selected": true
    },
    {
      "id": "aiModel-1753200684599",
      "type": "aiModel",
      "position": {
        "x": 1136.7964055732093,
        "y": -161.3001601059915
      },
      "data": {
        "nodeName": "Title Generator",
        "imageUrls": [],
        "settings": {
          "temperature": 1,
          "top_p": 1,
          "top_k": 0,
          "frequency_penalty": 0,
          "presence_penalty": 0,
          "repetition_penalty": 1,
          "max_tokens": 8192
        },
        "content": "You are a headline optimization specialist for a tech blog.\n\nTASK: Create a compelling, SEO-friendly title for the tech news article below.\n\nREQUIREMENTS:\n1. Length: 40-60 characters maximum\n2. Include 1-2 relevant tech keywords from the article\n3. Use action words or numbers to increase click-through rate\n4. Add exactly ONE emoji that matches the main theme\n5. Make it intriguing enough to encourage clicks\n6. Avoid clickbait or misleading elements\n\nFORMAT: Return ONLY the finished title with emoji, nothing else.\n\nARTICLE CONTENT:\n[Input from aiModel-1753200539633]",
        "openaiTools": {
          "webSearch": {
            "enabled": false,
            "contextSize": "medium",
            "location": {
              "city": "",
              "region": "",
              "country": ""
            }
          },
          "fileSearch": {
            "enabled": false,
            "vectorStoreId": "",
            "maxResults": 5
          }
        },
        "workflowId": null,
        "model": "google/gemma-3-12b-it:free"
      },
      "measured": {
        "width": 380,
        "height": 473
      }
    },
    {
      "id": "textAnnotation-intro",
      "type": "textAnnotation",
      "position": {
        "x": 153.85419125330122,
        "y": -293.74382272210323
      },
      "data": {
        "content": "<p><strong class=\"ql-size-huge\">RSS Feed Automation Workflow Tutorial</strong></p>",
        "fontSize": 24,
        "size": {
          "width": 779,
          "height": "auto"
        }
      },
      "measured": {
        "width": 779,
        "height": 74
      }
    },
    {
      "id": "stickyNote-trigger",
      "type": "stickyNote",
      "position": {
        "x": -86.92997643683145,
        "y": -128.04283220022188
      },
      "data": {
        "content": "<p>STEP 1: RSS TRIGGER This node pulls the latest articles from TechCrunch automatically. Try it with different RSS feeds by changing the URL in settings. Polling interval determines how often it checks for updates.</p>",
        "color": "#b3e5fc",
        "size": {
          "width": 220,
          "height": 180
        }
      },
      "measured": {
        "width": 220,
        "height": 180
      }
    },
    {
      "id": "stickyNote-ai1",
      "type": "stickyNote",
      "position": {
        "x": 638.6600801021543,
        "y": -62.63716949145189
      },
      "data": {
        "content": "<p>STEP 2: CONTENT GENERATION This AI node transforms raw RSS data into a well-structured article. The prompt is detailed to ensure consistent quality output. Try different models to compare results!</p>",
        "color": "#c8e6c9",
        "size": {
          "width": 220,
          "height": 170
        }
      },
      "measured": {
        "width": 220,
        "height": 196
      }
    },
    {
      "id": "stickyNote-ai2",
      "type": "stickyNote",
      "position": {
        "x": 1155.016062075083,
        "y": -324.6112013287774
      },
      "data": {
        "content": "<p>STEP 3: TITLE CREATION This node analyzes the article to generate an engaging title. Catchy titles with emojis can increase click-through rates.</p>",
        "color": "#f8bbd0",
        "size": {
          "width": 220,
          "height": 150
        }
      },
      "measured": {
        "width": 220,
        "height": 150
      }
    },
    {
      "id": "stickyNote-post",
      "type": "stickyNote",
      "position": {
        "x": 1618.716208265601,
        "y": 18.867983979569146
      },
      "data": {
        "content": "<p>STEP 4: WORDPRESS PUBLISHING The final node creates a draft post with your generated content. Change status to 'publish' when ready for automatic posting.</p>",
        "color": "#e1bee7",
        "size": {
          "width": 220,
          "height": 150
        }
      },
      "measured": {
        "width": 220,
        "height": 157
      }
    },
    {
      "id": "text-1753286112232",
      "type": "textAnnotation",
      "position": {
        "x": 153.86752499142574,
        "y": -242.20060648416998
      },
      "data": {
        "content": "<p><strong class=\"ql-size-huge\">To run the workflow, click on Execute Workflow on the side bar!</strong></p>",
        "fontSize": 14,
        "size": {
          "width": 853,
          "height": "auto"
        }
      },
      "measured": {
        "width": 853,
        "height": 74
      }
    }
  ],
  "edges": [
    {
      "id": "xy-edge__trigger-1753200513904a-aiModel-1753200539633",
      "source": "trigger-1753200513904",
      "target": "aiModel-1753200539633",
      "sourceHandle": "a",
      "targetHandle": null
    },
    {
      "id": "xy-edge__aiModel-1753200539633a-post-1753200661952",
      "source": "aiModel-1753200539633",
      "target": "post-1753200661952",
      "sourceHandle": "a",
      "targetHandle": null
    },
    {
      "id": "xy-edge__aiModel-1753200539633a-aiModel-1753200684599",
      "source": "aiModel-1753200539633",
      "target": "aiModel-1753200684599",
      "sourceHandle": "a",
      "targetHandle": null
    },
    {
      "id": "xy-edge__aiModel-1753200684599a-post-1753200661952",
      "source": "aiModel-1753200684599",
      "target": "post-1753200661952",
      "sourceHandle": "a",
      "targetHandle": null
    }
  ]
  };

  // Add handlers to annotation nodes
  const processedNodes = template.nodes.map(node => {
    if (['stickyNote', 'textAnnotation', 'shape'].includes(node.type)) {
      return {
        ...node,
        data: {
          ...node.data,
          onChange: (id, updates) => {
            setNodes(nds => 
              nds.map(n => 
                n.id === id 
                  ? { 
                      ...n, 
                      data: { 
                        ...n.data, 
                        ...updates,
                        onChange: n.data.onChange,
                        onDelete: n.data.onDelete 
                      } 
                    }
                  : n
              )
            );
          },
          onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
        }
      };
    }
    // Handle other node types
    return {
      ...node,
      data: {
        ...node.data,
        updateNodeData,
        onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
      }
    };
  });

  setNodes(processedNodes);
  setEdges(template.edges);
  setWorkflowName(template.name);
  
  // Show success message
  message.success('Your first workflow template has been loaded! Click "Execute Workflow" to test it.');
};

const handleGenerateWorkflow = async (prompt) => {
  if (!prompt.trim()) {
    message.error('Please enter a workflow description');
    return;
  }

  try {
    const response = await adminInstance.post(
      `${config.apiSettings.root}wp-ai-workflows/v1/generate-workflow`,
      { prompt },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.apiSettings.nonce
        }
      }
    );

    if (!response.data.success && response.data.error) {
      throw new Error(response.data.error.message || 'Failed to generate workflow');
    }

    const workflow = response.data;
    
    // Validate workflow structure
    if (!workflow.nodes || !workflow.edges) {
      throw new Error('The generated workflow structure was invalid. Please try again with a different description.');
    }

    // Check for empty workflow
    if (workflow.nodes.length === 0) {
      throw new Error('No workflow nodes were generated. Please try with a more specific description.');
    }

    // Load the workflow into the editor
    handleImport(workflow);
    message.success('Workflow generated successfully!');
    
    // Close the template modal after successful generation
    setIsTemplateSelectionModalVisible(false);
    
  } catch (error) {
    console.error('Error generating workflow:', error);
    
    let errorMessage = 'Failed to generate workflow: ';
    
    if (error.response) {
      if (error.response.data && error.response.data.error) {
        errorMessage += error.response.data.error.message || 'Server error occurred';
      } else if (error.response.status === 500) {
        errorMessage = 'The workflow generation failed. This could be due to:' +
                      '\n- Complex or ambiguous requirements' +
                      '\n- Server processing limitations' +
                      '\n\nPlease try again with a simpler or more specific description.';
      } else {
        errorMessage += `Server error (${error.response.status})`;
      }
    } else if (error.request) {
      errorMessage += 'No response from server. Please check your connection.';
    } else {
      errorMessage += error.message || 'An unexpected error occurred';
    }

    Modal.error({
      title: 'Workflow Generation Failed',
      content: (
        <div>
          <p>{errorMessage}</p>
          <Alert
            type="info"
            message="Tips for successful workflow generation:"
            description={
              <ul>
                <li>Be specific about what you want the workflow to do</li>
                <li>Explain your goal clearly</li>
                <li>Specify input sources and desired outputs</li>
              </ul>
            }
          />
        </div>
      ),
      width: 500
    });
  }
};

  return (
    <div
    className="reactflow-wrapper" 
    style={{ 
      height: '90vh',
      position: 'relative',
       overflow: 'hidden'
    }}>
      <div style={{ 
        display: 'flex',
        height: '100%',
      }}>
        {/* Sidebar */}
        <div style={{ 
          width: '200px', 
          borderRight: '1px solid #ddd', 
          height: '100%', 
          zIndex: 5,
          flexShrink: 0
        }}>
          <Sidebar
            updateNodeData={updateNodeData}
            onSave={onSave}
            onExecute={executeWorkflow}
            onUpdate={onUpdate}
            onSaveAsTemplate={handleSaveAsTemplate}
            isEditMode={isEditMode}
            isExecuting={isExecuting}
            isEditingTemplate={isEditingTemplate}
            isSavedWorkflow={isSavedWorkflow}
            nodes={nodes}
            edges={edges}
            workflowName={workflowName}
            onActionModalOpen={handleActionModalOpen} 
            darkMode={colorMode === 'dark'}
          />
        </div>
  
        {/* Right side container */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Name header */}
          <WorkflowNameHeader
          initialName={workflowName}
          isEditMode={isEditMode}
          onNameChange={(newName) => {
            setWorkflowName(newName);
            if (isEditMode || isSavedWorkflow) {
              const flow = reactFlowInstance.toObject();
              const updatedWorkflow = {
                id: workflowId,
                name: newName,
                nodes: flow.nodes,
                edges: flow.edges,
                viewport: flow.viewport
              };
              
              adminInstance.put(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`, 
                updatedWorkflow,
                {
                  headers: {
                    'X-WP-Nonce': config.apiSettings.nonce
                  }
                }
              ).then(() => {
                message.success('Workflow name updated successfully');
              }).catch((error) => {
                console.error('Error updating workflow name:', error);
                message.error('Failed to update workflow name');
              });
            }
          }}
        />
          
          {/* Execution Status Bar */}
          <ExecutionStatusBar 
            isExecuting={isExecuting}
            currentNode={currentExecutingNode}
            completionTime={completionTime}
            hasError={executionError}
            progress={executionProgress}
            onViewDetails={() => {
              if (executionId) { // Make sure we have an executionId
                viewExecutionDetails(executionId);
              }
            }}
          />

          <WorkflowDetailsModal
            execution={selectedExecution}
            visible={isDetailsModalVisible}
            onClose={() => {
              setIsDetailsModalVisible(false);
              setSelectedExecution(null);
            }}
          />
  
          {/* Flow container */}
          <div style={{ 
            height: '100%',
            width: '100%'
          }} ref={reactFlowWrapper}>

          <ReactFlow
          key={refreshKey}
            nodes={nodes}
            edges={edges.map(edge => ({
              ...edge,
              data: { 
                onDelete: handleEdgeDelete,
                colorMode: colorMode
              }
            }))}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            colorMode={colorMode}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{ 
              type: 'default',
              animated: false
            }}
            onlyRenderVisibleElements={true}
            nodesFocusable={false}
            edgesFocusable={false}
            elevateNodesOnSelect={false}
            minZoom={0.1} 
            maxZoom={4} 
          >
            <Controls />
            <MiniMap pannable />
            <Background variant="dots" gap={12} size={1} />
            {
              <WorkflowAssistant
                ref={workflowAssistantRef}
                workflow={{
                    nodes,
                    edges,
                    viewport: reactFlowInstance?.getViewport(),
                    name: workflowName,
                    id: workflowId,
                    isExecuting,
                    isSaved: isSavedWorkflow,
                    onWorkflowUpdate: (updatedWorkflow) => {
                        if (!updatedWorkflow || !updatedWorkflow.nodes || !updatedWorkflow.edges) {
                            console.error('Invalid workflow data received');
                            return;
                        }
                        
                        try {
                            // Check for new nodes and edges
                            const existingNodeIds = nodes.map(n => n.id);
                            const existingEdgeIds = edges.map(e => e.id);
                            
                            const newNodeIds = updatedWorkflow.nodes
                                .filter(n => !existingNodeIds.includes(n.id))
                                .map(n => n.id);
                            
                            const newEdgeIds = updatedWorkflow.edges
                                .filter(e => !existingEdgeIds.includes(e.id))
                                .map(e => e.id);
                            
                            // Prepare nodes
                            const preparedNodes = updatedWorkflow.nodes.map(node => {
                                return {
                                    ...node,
                                    type: node.type || (node.id.includes('-') ? node.id.split('-')[0] : 'default'),
                                    data: {
                                        ...node.data,
                                        updateNodeData,
                                        onDelete: (id) => setNodes(nds => nds.filter(n => n.id !== id))
                                    }
                                };
                            });
                            
                            // Prepare edges
                            const preparedEdges = updatedWorkflow.edges.map(edge => {
                                return {
                                    ...edge,
                                    data: {
                                        onDelete: handleEdgeDelete,
                                        colorMode
                                    }
                                };
                            });
                            
                            // Clear and set approach with delays
                            setNodes([]);
                            setEdges([]);
                            
                            setTimeout(() => {
                                setNodes(preparedNodes);
                                
                                setTimeout(() => {
                                    setEdges(preparedEdges);
                                    
                                    setTimeout(() => {
                                        // ReactFlow instance operations
                                        if (reactFlowInstance) {
                                            try {
                                                // Force a rerender by manipulating viewport
                                                const viewport = reactFlowInstance.getViewport();
                                                reactFlowInstance.setViewport({
                                                    x: viewport.x + 0.1,
                                                    y: viewport.y + 0.1,
                                                    zoom: viewport.zoom
                                                });
                                                
                                                // Fit view
                                                reactFlowInstance.fitView({ padding: 0.2 });
                                                
                                                setTimeout(() => {
                                                    const finalRfNodes = reactFlowInstance.getNodes();
                                                    const finalRfEdges = reactFlowInstance.getEdges();
                                                    
                                                    // If still missing nodes, try a more forceful approach
                                                    if (finalRfNodes.length !== preparedNodes.length) {
                                                        // This would be a good place to implement the component reload pattern
                                                        // or try other forceful refresh methods
                                                    }
                                                }, 500);
                                            } catch (error) {
                                                console.error('Error in ReactFlow operations:', error);
                                            }
                                        } else {
                                            console.error('ReactFlow instance not available');
                                        }
                                    }, 200);
                                }, 100);
                            }, 100);
                            
                            // Show success message
                            message.success(
                                `Workflow updated: ${newNodeIds.length} new node(s) and ${newEdgeIds.length} new connection(s).`
                            );
                        } catch (error) {
                            console.error('Error updating workflow:', error);
                            message.error('Failed to update workflow');
                        }
                    }
                }}
                selectedNode={getSelectedNode()}
                colorMode={colorMode}
                onNodeSelect={(node) => {
                    reactFlowInstance?.setNodes((nds) =>
                        nds.map((n) => ({
                            ...n,
                            selected: n.id === (node?.id || null)
                        }))
                    );
                }}
            /> }
              <AnnotationToolbar />
            
          </ReactFlow>
          </div>
        </div>
      </div>
      <TemplateModal
        visible={isTemplateSelectionModalVisible}
        onCancel={() => setIsTemplateSelectionModalVisible(false)}
        onSelectTemplate={handleSelectTemplate}
        onImport={handleImport}
        onCreateNew={handleCreateNew}
        onGenerate={handleGenerateWorkflow} 
      />

      <Modal
        title="Save as Template"
        visible={isSaveTemplateModalVisible}
        onOk={handleTemplateSave}
        onCancel={() => setIsSaveTemplateModalVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Template Name" required>
            <Input 
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name"
            />
          </Form.Item>
          <Form.Item label="Template Description">
            <Input.TextArea
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Enter template description (optional)"
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        className="workflow-name-input"
        title="Save Workflow"
        visible={isModalVisible}
        onOk={handleSaveWorkflow}
        onCancel={() => setIsModalVisible(false)}
      >
        <Input
          placeholder="Enter workflow name"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
        />
      </Modal>
      <WorkflowActionsModal
          visible={isActionsModalVisible}
          onCancel={() => setIsActionsModalVisible(false)}
          onExport={handleExport} 
          onImport={handleImport}
          workflowName={workflowName}
          workflowId={workflowId}
          onOpenEmbedModal={() => {
            setIsActionsModalVisible(false);
            setEmbedModalVisible(true);
          }}
        />

      {workflowId && (
      <EmbedCodeModal
        visible={embedModalVisible}
        workflowId={workflowId}
        onClose={() => setEmbedModalVisible(false)}
      />
    )}

      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000, display: 'flex', gap: '12px', alignItems: 'center' }}>
      <Switch
        checked={colorMode === 'dark'}
        onChange={toggleDarkMode}
        checkedChildren={<MoonOutlined />}
        unCheckedChildren={<SunOutlined />}
      />
      
      <Button
        type="primary"
        icon={<BulbOutlined />}
        onClick={() => window.open('https://wpaiworkflowautomation.com/docs/', '_blank')}
      >
        Documentation
      </Button>
      </div>
    </div>
  );
};

const WorkflowBuilder = () => (
  <ReactFlowProvider>
    <WorkflowBuilderInner />
  </ReactFlowProvider>
);

export default WorkflowBuilder;